import { useEffect, useState } from 'react'
import posthog from 'posthog-js'
import { FEATURE_FLAGS } from '@/shared/constants/feature-flags'
import { Capacitor } from '@capacitor/core'
import { App } from '@capacitor/app'

export type AppVersionStatus = 'loading' | 'ok' | 'warn' | 'block'

interface AppVersionMessage {
  title?: string
  description?: string
}

export interface AppVersionPayload {
  'message-en'?: AppVersionMessage
  'message-es'?: AppVersionMessage
}

export const APP_VERSION_VARIANT = {
  BLOCK: 'block',
  WARN: 'warn',
  OK: 'ok',
} as const

export const STORE_URLS = {
  ios: 'https://apps.apple.com/us/app/kanguro/id1625776781',
  android:
    'https://play.google.com/store/apps/details?id=com.insurtech.kanguro',
} as const

export function getStoreUrl(): string {
  const platform = Capacitor.getPlatform()
  if (platform === 'android') return STORE_URLS.android
  if (platform === 'ios') return STORE_URLS.ios
  const isAndroid = /android/i.test(navigator.userAgent)
  return isAndroid ? STORE_URLS.android : STORE_URLS.ios
}

export function useAppVersionFlag(): {
  status: AppVersionStatus
  payload: AppVersionPayload | undefined
} {
  const [status, setStatus] = useState<AppVersionStatus>('loading')
  const [payload, setPayload] = useState<AppVersionPayload | undefined>(
    undefined,
  )

  useEffect(() => {
    const unsubscribeFlags = posthog.onFeatureFlags(() => {
      const result = posthog.getFeatureFlagResult(
        FEATURE_FLAGS.APP_VERSION_VALID,
      )
      const flagPayload = result?.payload as AppVersionPayload | undefined
      setPayload(flagPayload)

      switch (result?.variant) {
        case APP_VERSION_VARIANT.BLOCK:
          setStatus(APP_VERSION_VARIANT.BLOCK)
          break
        case APP_VERSION_VARIANT.WARN:
          setStatus(APP_VERSION_VARIANT.WARN)
          break
        default:
          setStatus(APP_VERSION_VARIANT.OK)
      }
    })

    // Register the app version then reload so the flag re-evaluates with the correct version
    async function registerVersion() {
      if (Capacitor.isNativePlatform()) {
        const info = await App.getInfo()
        posthog.register({ app_version: info.version })
        posthog.reloadFeatureFlags()
      }
    }

    registerVersion()

    const listener = App.addListener('appStateChange', ({ isActive }) => {
      if (isActive) posthog.reloadFeatureFlags()
    })

    return () => {
      unsubscribeFlags?.()
      listener.then((l) => l.remove())
    }
  }, [])

  return { status, payload }
}
